import * as React from 'react'

import { F0WizardForm } from '@factorialco/f0-react'
import type { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import type { InMemoryAdditionalCompensation } from 'modules/contracts/lib/contractFlowContext'
import { parseDateString } from 'modules/contracts/lib/utils'

import { useAdditionalCompensationWizardForm } from './useAdditionalCompensationWizardForm'
import {
  type AdditionalCompensationWizardInitialValues,
  type BasicContext,
  type BasicStepValues,
  FIXED_AMOUNT_STRATEGY,
  type RawDefinitionStepValues,
  isBasicStepValues,
  isRawDefinitionStepValues,
  isSupportedAmountStrategyType,
} from './lib/additionalCompensationWizard.shared'
import {
  buildMappedAdditionalCompensation,
  buildWizardInitialValues,
  createBasicContextFromValues,
  normalizeDefinitionValues,
} from './lib/additionalCompensationWizard.utils'
import { getFirstPaymentDate } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/lib/getFirstPaymentDate'

type AdditionalCompensationWizardModalProps = {
  title: string
  formName: string
  submitLabel: string
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  onClose?: () => void
  effectiveOn?: string
  contractVersionId: string
  additionalCompensation?: InMemoryAdditionalCompensation
  leaveTypeOptions: Array<{ id: string; label: string }>
  onSave: (additionalCompensation: InMemoryAdditionalCompensation) => void
}

function AdditionalCompensationWizardModal({
  title,
  formName,
  submitLabel,
  onClose,
  payrollConcepts,
  effectiveOn,
  contractVersionId,
  additionalCompensation,
  leaveTypeOptions,
  onSave,
}: AdditionalCompensationWizardModalProps) {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const resolvedPayrollConcepts = React.useMemo<PayrollConceptSelectFieldFragment[]>(() => {
    if (!additionalCompensation?.payrollConcept) {
      return payrollConcepts
    }

    const hasSelectedPayrollConcept = payrollConcepts.some(
      (payrollConcept) => payrollConcept.id === additionalCompensation.payrollConceptId
    )

    if (hasSelectedPayrollConcept) {
      return payrollConcepts
    }

    const fallbackPayrollConcept: PayrollConceptSelectFieldFragment = {
      id: additionalCompensation.payrollConceptId,
      label: additionalCompensation.payrollConcept.label,
      name: additionalCompensation.payrollConcept.name,
      unitType: additionalCompensation.payrollConcept.unitType,
      category: null,
    }

    return [...payrollConcepts, fallbackPayrollConcept]
  }, [additionalCompensation, payrollConcepts])

  const payrollConceptOptions = useCategorizedPayrollConcepts(resolvedPayrollConcepts)

  const defaultFirstPaymentDate = React.useMemo(() => {
    const parsedFirstPaymentDate = parseDateString(getFirstPaymentDate(effectiveOn))
    return parsedFirstPaymentDate ? parsedFirstPaymentDate.toDate() : undefined
  }, [effectiveOn])

  const initialValues = React.useMemo<AdditionalCompensationWizardInitialValues | undefined>(
    () =>
      buildWizardInitialValues({
        additionalCompensation,
        defaultFirstPaymentDate,
      }),
    [additionalCompensation, defaultFirstPaymentDate]
  )

  const initialBasicValues = React.useMemo(
    () =>
      initialValues?.basic && isBasicStepValues(initialValues.basic) ? initialValues.basic : null,
    [initialValues?.basic]
  )

  const [basicContext, setBasicContext] = React.useState<BasicContext>({
    amountStrategyType: initialBasicValues?.amountStrategyType ?? FIXED_AMOUNT_STRATEGY,
    payrollConceptId: initialBasicValues?.payrollConceptId,
  })

  const basicValuesRef = React.useRef<BasicStepValues | null>(initialBasicValues)

  const handleSubmit = React.useCallback(
    async ({
      sectionId,
      data,
    }: {
      sectionId: 'basic' | 'definition'
      data: BasicStepValues | RawDefinitionStepValues
    }) => {
      if (sectionId === 'basic') {
        if (!isBasicStepValues(data)) {
          return { success: false }
        }

        const basicValues = data

        basicValuesRef.current = basicValues
        setBasicContext((current) => {
          const nextContext = createBasicContextFromValues(basicValues)

          if (
            current.amountStrategyType === nextContext.amountStrategyType &&
            current.payrollConceptId === nextContext.payrollConceptId
          ) {
            return current
          }

          return nextContext
        })

        return { success: true }
      }

      const basicValues = basicValuesRef.current

      if (!basicValues) {
        return { success: false }
      }

      if (!isSupportedAmountStrategyType(basicValues.amountStrategyType)) {
        return { success: false }
      }

      if (!isRawDefinitionStepValues(data)) {
        return { success: false }
      }

      const nextAdditionalCompensation = buildMappedAdditionalCompensation({
        basicValues,
        definitionValues: normalizeDefinitionValues(data),
        companyId,
        contractVersionId,
        payrollConcepts: resolvedPayrollConcepts,
        createTemporaryId: () => uniqueId(),
        additionalCompensation,
      })

      onSave(nextAdditionalCompensation)
      onClose?.()

      return { success: true }
    },
    [additionalCompensation, contractVersionId, onClose, onSave, resolvedPayrollConcepts]
  )

  const { formDefinition, steps } = useAdditionalCompensationWizardForm({
    formName,
    submitLabel,
    initialValues,
    payrollConcepts: resolvedPayrollConcepts,
    payrollConceptOptions,
    effectiveOn,
    defaultFirstPaymentDate,
    basicContext,
    leaveTypeOptions,
    onSubmit: handleSubmit,
  })

  return (
    <F0WizardForm
      isOpen
      onClose={onClose}
      title={title}
      formDefinition={formDefinition}
      steps={steps}
      nextLabel={i18n.t('common.continue')}
      previousLabel={i18n.t('common.back')}
      width="xl"
    />
  )
}

export { AdditionalCompensationWizardModal }
